import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import InfoButton from './InfoButton';
import { fieldConfigsInfo } from './operationsInfo';
import {
  getOperationFieldConfigs,
  createOperationFieldConfig,
  updateOperationFieldConfig,
  setOperationFieldConfigStatus,
  getOperationRecords,
  getProductsDictionary,
  matchDevicesForOperation,
  getFieldMissionDevicesByOfcId,
} from '../../services/adminService';
import { OperationDictionary } from '../../types/operation';
import { getRegisteredFields } from '../../services/fieldService';
import { Field } from '../../types/field';
import {
  OperationFieldConfig,
  OperationFieldConfigPayload,
  OperationFieldConfigStatus,
  OperationRecord,
  FieldMissionDevice,
  DeviceMatchingResponse,
  RequiredDeviceMatch,
} from '../../types/device';
import {
  AdminTable,
  AdminTableRow,
  AdminPrimaryButton,
  AdminSecondaryButton,
  AdminInlineButton,
  AdminDangerButton,
  AdminForm,
  AdminFormGroup,
  AdminFormLabel,
  AdminFormInput,
  AdminFormSelect,
  AdminStatusBadge,
  AdminChip,
  AdminErrorMessage,
  AdminSuccessMessage,
  AdminLoadingMessage,
  AdminFormActions,
  AdminEmptyState,
  adminColors,
} from '../../styles/adminStyles';

const Container = styled.div`
  margin-bottom: 20px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const PageTitle = styled.h2`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.75rem;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 0;
  font-weight: 700;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 180px 80px 100px 90px 160px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 164, 255, 0.05) 100%);
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #ffffff;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 14px 20px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FilterWrapper = styled.div`
  position: relative;
  min-width: 300px;
`;

const FilterInput = styled.input`
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  padding: 10px 14px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${adminColors.primary};
  }

  &::placeholder {
    color: ${adminColors.textMuted};
  }
`;

const FilterDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(0, 30, 60, 0.95);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  margin-top: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 100;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const FilterOption = styled.div<{ $isSelected?: boolean }>`
  padding: 10px 14px;
  cursor: pointer;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$isSelected ? adminColors.primary : '#ffffff'};
  background: ${props => props.$isSelected ? 'rgba(0, 212, 255, 0.1)' : 'transparent'};

  &:hover {
    background: rgba(0, 212, 255, 0.15);
  }
`;

const FilterLabel = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #ffffff;
  font-weight: 700;
  text-transform: uppercase;
`;

const TableRow = styled(AdminTableRow)`
  grid-template-columns: 120px 1fr 180px 80px 100px 90px 160px;
  color: #ffffff;
  font-weight: 600;
  font-size: 13px;

  & > div {
    color: #ffffff;
    font-size: 13px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Section = styled.div`
  margin-top: 24px;
  padding: 16px;
  background: rgba(0, 30, 60, 0.3);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 10px;
`;

const SectionTitle = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 16px;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
  font-weight: 700;
`;

const DeviceCard = styled.div`
  background: rgba(0, 30, 60, 0.3);
  border: 1px solid rgba(0, 212, 255, 0.15);
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 12px;
`;

const DeviceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const DeviceTitle = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ffffff;
  font-weight: 700;
  text-transform: uppercase;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SmallFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SmallLabel = styled.label`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #ffffff;
  font-weight: 700;
  text-transform: uppercase;
`;


const ModeButton = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? 'rgba(0, 212, 255, 0.2)' : 'transparent'};
  border: 1px solid ${props => props.$active ? 'rgba(0, 212, 255, 0.5)' : 'rgba(0, 212, 255, 0.3)'};
  border-radius: 4px;
  padding: 8px 14px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(0, 212, 255, 0.5);
    background: rgba(0, 212, 255, 0.1);
  }
`;

const QueueBadge = styled.span`
  background: rgba(76, 175, 80, 0.2);
  border: 1px solid #4CAF50;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 9px;
  color: #4CAF50;
  text-transform: uppercase;
  margin-left: 8px;
`;

const InfoBox = styled.div`
  background: rgba(0, 30, 60, 0.3);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 16px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
`;

const SearchInput = styled.input`
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  padding: 10px 12px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;
  width: 100%;
  margin-bottom: 8px;

  &:focus {
    outline: none;
    border-color: ${adminColors.primary};
  }

  &::placeholder {
    color: ${adminColors.textMuted};
  }
`;

const DataTable = styled.div`
  border: 1px solid rgba(0, 212, 255, 0.3);
  background: rgba(0, 30, 60, 0.3);
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
`;

const WhiteEmptyState = styled.div`
  color: #ffffff;
  font-weight: 700;
  font-size: 14px;
  padding: 40px;
  text-align: center;
  font-family: 'Courier New', monospace;
`;

interface FormState {
  saleNumber?: string;
  fieldId: string;
  operationRecordId: string;
  fmdIds: string[];
  activationDate?: string;
  status?: OperationFieldConfigStatus;
}

const initialFormState: FormState = {
  saleNumber: '',
  fieldId: '',
  operationRecordId: '',
  fmdIds: [],
  activationDate: undefined,
  status: undefined,
};

const OperationFieldConfigManager: React.FC = () => {
  const [configs, setConfigs] = useState<OperationFieldConfig[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [editedConfig, setEditedConfig] = useState<OperationFieldConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [operationRecords, setOperationRecords] = useState<OperationRecord[]>([]);
  const [selectedOperation, setSelectedOperation] = useState<OperationRecord | null>(null);
  const [fields, setFields] = useState<Field[]>([]);
  const [products, setProducts] = useState<OperationDictionary[]>([]);
  const [fieldSearch, setFieldSearch] = useState('');
  const [operationSearch, setOperationSearch] = useState('');
  const [filterFieldId, setFilterFieldId] = useState<string>('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  // Device Matching state (для автоматического подбора устройств)
  const [deviceMatching, setDeviceMatching] = useState<DeviceMatchingResponse | null>(null);
  const [loadingDevices, setLoadingDevices] = useState(false);
  // Для редактирования - устройства привязанные к конфигурации
  const [fieldMissionDevices, setFieldMissionDevices] = useState<FieldMissionDevice[]>([]);

  const pageSize = 20;

  const loadConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getOperationFieldConfigs(page, pageSize);
      setConfigs(data.records || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error('Failed to load configs', err);
      setError(err instanceof Error ? err.message : 'Unable to load configs');
    } finally {
      setLoading(false);
    }
  };

  const loadOperationRecords = async () => {
    try {
      const data = await getOperationRecords(1, 100);
      setOperationRecords(data.records || []);
    } catch (err) {
      console.error('Failed to load operation records', err);
    }
  };

  const loadFields = async () => {
    try {
      const data = await getRegisteredFields();
      setFields(data || []);
    } catch (err) {
      console.error('Failed to load fields', err);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProductsDictionary();
      setProducts(data || []);
    } catch (err) {
      console.error('Failed to load products', err);
    }
  };

  // Загрузить автоматически подобранные устройства для операции
  const loadDeviceMatching = async (operationRecordId: string) => {
    setLoadingDevices(true);
    try {
      const data = await matchDevicesForOperation(operationRecordId);
      setDeviceMatching(data);
      // Автоматически заполняем fmdIds из подобранных устройств
      const matchedFmdIds = data.requiredDevices
        .filter(d => d.matchedFmd)
        .map(d => d.matchedFmd!.fmdId);
      setFormState(prev => ({ ...prev, fmdIds: matchedFmdIds }));
    } catch (err) {
      console.error('Failed to match devices', err);
      setDeviceMatching(null);
    } finally {
      setLoadingDevices(false);
    }
  };

  // Загрузить устройства привязанные к конкретной конфигурации (для редактирования)
  const loadDevicesForConfig = async (ofcId: string) => {
    try {
      const data = await getFieldMissionDevicesByOfcId(ofcId);
      setFieldMissionDevices(data.records || []);
    } catch (err) {
      console.error('Failed to load devices for config', err);
      setFieldMissionDevices([]);
    }
  };

  const getOperationName = (operationId: string | undefined) => {
    if (!operationId) return '-';
    const product = products.find(p => p.id === operationId);
    return product ? product.title : operationId;
  };

  const getFieldName = (fieldId: string) => {
    const field = fields.find(f => f.id === fieldId);
    return field ? `${field.name} (${field.state})` : fieldId;
  };

  useEffect(() => {
    loadConfigs();
  }, [page]);

  useEffect(() => {
    loadOperationRecords();
    loadFields();
    loadProducts();
  }, []);

  const filteredFields = fields
    .filter(f =>
      f.name.toLowerCase().includes(fieldSearch.toLowerCase()) ||
      f.state.toLowerCase().includes(fieldSearch.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  // Получаем уникальные поля из конфигов для фильтра
  const fieldsInConfigs = Array.from(new Set(configs.map(c => c.fieldId)))
    .map(fieldId => {
      const field = fields.find(f => f.id === fieldId);
      return { id: fieldId, name: field ? `${field.name} (${field.state})` : fieldId };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  // Фильтруем список полей для dropdown по поисковому запросу
  const filteredFieldsForDropdown = fieldsInConfigs.filter(f =>
    f.name.toLowerCase().includes(filterSearch.toLowerCase())
  );

  // Фильтруем конфиги по выбранному полю
  const filteredConfigs = filterFieldId
    ? configs.filter(c => c.fieldId === filterFieldId)
    : configs;

  // Получаем название выбранного поля для отображения в инпуте
  const selectedFieldName = filterFieldId
    ? fieldsInConfigs.find(f => f.id === filterFieldId)?.name || ''
    : '';

  const filteredOperations = operationRecords
    .filter(op => {
      const productName = getOperationName(op.operationId).toLowerCase();
      return productName.includes(operationSearch.toLowerCase()) ||
        op.uniqueCode.toLowerCase().includes(operationSearch.toLowerCase());
    })
    .sort((a, b) => getOperationName(a.operationId).localeCompare(getOperationName(b.operationId)));

  const handleCreateClick = async () => {
    setEditedConfig(null);
    setFormState(initialFormState);
    setSelectedOperation(null);
    setFieldSearch('');
    setOperationSearch('');
    setError(null);
    setSuccess(null);
    setDeviceMatching(null);
    setFieldMissionDevices([]);
    setModalOpen(true);
  };

  const handleEditClick = async (config: OperationFieldConfig) => {
    setEditedConfig(config);
    const operation = operationRecords.find(o => o.id === config.operationRecordId);
    setSelectedOperation(operation || null);
    setFormState({
      fieldId: config.fieldId,
      operationRecordId: config.operationRecordId,
      fmdIds: config.fmdIds || [],
      activationDate: config.activationDate ? config.activationDate.split('T')[0] : undefined,
      status: config.status,
    });
    setFieldSearch('');
    setOperationSearch('');
    setError(null);
    setSuccess(null);
    setDeviceMatching(null);
    await loadDevicesForConfig(config.id); // Загружаем устройства привязанные к этой конфигурации
    setModalOpen(true);
  };

  const handleOperationChange = async (operationId: string) => {
    const operation = operationRecords.find(o => o.id === operationId);
    setSelectedOperation(operation || null);
    setFormState({
      ...formState,
      operationRecordId: operationId,
      fmdIds: [],
    });
    // Загружаем автоматически подобранные устройства для этой операции
    if (operationId) {
      await loadDeviceMatching(operationId);
    } else {
      setDeviceMatching(null);
    }
  };

  const handleFieldChange = (fieldId: string) => {
    setFormState({
      ...formState,
      fieldId,
    });
  };

  const getStatusColor = (status: OperationFieldConfigStatus): 'active' | 'inactive' | 'warning' => {
    switch (status) {
      case 'active': return 'active';
      case 'testing': return 'warning';
      case 'pending': return 'inactive';
      default: return 'inactive';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formState.fieldId.trim() || !formState.operationRecordId) {
      setError('Field ID and Operation are required');
      return;
    }

    if (formState.fmdIds.length === 0) {
      setError('At least one Field Mission Device must be selected');
      return;
    }

    const payload: OperationFieldConfigPayload = {
      saleNumber: formState.saleNumber?.trim() || undefined,
      fieldId: formState.fieldId.trim(),
      operationRecordId: formState.operationRecordId,
      fmdIds: formState.fmdIds,
      activationDate: formState.activationDate,
    };

    setSaving(true);
    try {
      if (editedConfig) {
        await updateOperationFieldConfig(editedConfig.id, payload);
        // Update status if changed
        if (formState.status && formState.status !== editedConfig.status) {
          await setOperationFieldConfigStatus(editedConfig.id, formState.status);
        }
        setSuccess('Configuration updated');
      } else {
        await createOperationFieldConfig(payload);
        setSuccess('Configuration created');
      }

      setModalOpen(false);
      setFormState(initialFormState);
      setEditedConfig(null);
      setSelectedOperation(null);
      setFieldMissionDevices([]);
      await loadConfigs();
    } catch (err) {
      console.error('Failed to save config', err);
      setError(err instanceof Error ? err.message : 'Unable to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <Container>
      <PageHeader>
        <PageTitle>Operation Field Configs</PageTitle>
        <InfoButton title="Field Configs Help" sections={fieldConfigsInfo} />
      </PageHeader>
      <HeaderActions>
        <div></div>
        <AdminPrimaryButton onClick={handleCreateClick}>+ Sell Operation</AdminPrimaryButton>
      </HeaderActions>

      {loading && <AdminLoadingMessage>Loading...</AdminLoadingMessage>}
      {error && !modalOpen && <AdminErrorMessage>{error}</AdminErrorMessage>}

      <FilterBar>
        <FilterLabel>Filter by Field:</FilterLabel>
        <FilterWrapper>
          <FilterInput
            type="text"
            placeholder="Search and select field..."
            value={filterFieldId ? selectedFieldName : filterSearch}
            onChange={(e) => {
              setFilterSearch(e.target.value);
              setFilterFieldId('');
              setFilterDropdownOpen(true);
            }}
            onFocus={() => setFilterDropdownOpen(true)}
            onBlur={() => setTimeout(() => setFilterDropdownOpen(false), 200)}
          />
          <FilterDropdown $isOpen={filterDropdownOpen}>
            <FilterOption
              $isSelected={!filterFieldId}
              onClick={() => {
                setFilterFieldId('');
                setFilterSearch('');
                setFilterDropdownOpen(false);
              }}
            >
              All Fields
            </FilterOption>
            {filteredFieldsForDropdown.map(f => (
              <FilterOption
                key={f.id}
                $isSelected={filterFieldId === f.id}
                onClick={() => {
                  setFilterFieldId(f.id);
                  setFilterSearch('');
                  setFilterDropdownOpen(false);
                }}
              >
                {f.name}
              </FilterOption>
            ))}
            {filteredFieldsForDropdown.length === 0 && filterSearch && (
              <FilterOption>No fields found</FilterOption>
            )}
          </FilterDropdown>
        </FilterWrapper>
        {filterFieldId && (
          <AdminSecondaryButton onClick={() => {
            setFilterFieldId('');
            setFilterSearch('');
          }}>
            Clear Filter
          </AdminSecondaryButton>
        )}
      </FilterBar>

      <DataTable>
        <TableHeader>
          <div>Sale #</div>
          <div>Operation</div>
          <div>Field</div>
          <div>Devices</div>
          <div>Purchased</div>
          <div>Status</div>
          <div>Actions</div>
        </TableHeader>
        {filteredConfigs.map((config) => (
          <TableRow key={config.id}>
            <div style={{ fontFamily: 'Courier New', fontSize: '13px', color: '#ffffff', fontWeight: 700 }}>
              {config.saleNumber}
            </div>
            <div style={{ fontSize: '13px', color: '#ffffff', fontWeight: 700 }}>{config.operationSnapshot.operationName}</div>
            <div style={{ fontSize: '13px', color: '#ffffff', fontWeight: 700 }}>{getFieldName(config.fieldId)}</div>
            <div>
              <AdminChip>{config.fmdIds?.length || 0}</AdminChip>
            </div>
            <div style={{ fontSize: '13px', color: '#ffffff', fontWeight: 700 }}>
              {new Date(config.purchasedAt).toLocaleDateString()}
            </div>
            <div>
              <AdminStatusBadge $status={getStatusColor(config.status)}>
                {config.status.toUpperCase()}
              </AdminStatusBadge>
            </div>
            <div>
              <AdminInlineButton onClick={() => handleEditClick(config)}>Edit</AdminInlineButton>
            </div>
          </TableRow>
        ))}
        {filteredConfigs.length === 0 && !loading && (
          <WhiteEmptyState>
            {filterFieldId ? 'No configurations found for selected field' : 'No configurations found'}
          </WhiteEmptyState>
        )}
      </DataTable>

      {totalPages > 1 && (
        <Pagination>
          <AdminSecondaryButton disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            Prev
          </AdminSecondaryButton>
          <span>Page {page} of {totalPages}</span>
          <AdminSecondaryButton disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            Next
          </AdminSecondaryButton>
        </Pagination>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditedConfig(null);
          setFormState(initialFormState);
          setSelectedOperation(null);
        }}
        title={editedConfig ? 'Edit Configuration' : 'Sell Operation to Field'}
      >
        <AdminForm onSubmit={handleSubmit}>
          {editedConfig ? (
            <>
              <InfoBox>
                <strong>Sale Number:</strong> {editedConfig.saleNumber}
              </InfoBox>
              <AdminFormGroup>
                <AdminFormLabel>Status</AdminFormLabel>
                <AdminFormSelect
                  value={formState.status || editedConfig.status}
                  onChange={(e) => setFormState({ ...formState, status: e.target.value as OperationFieldConfigStatus })}
                >
                  <option value="pending">Pending</option>
                  <option value="testing">Testing</option>
                  <option value="active">Active</option>
                </AdminFormSelect>
              </AdminFormGroup>
            </>
          ) : (
            <AdminFormGroup>
              <AdminFormLabel>Sale Number (optional - leave empty to generate)</AdminFormLabel>
              <AdminFormInput
                type="text"
                value={formState.saleNumber || ''}
                onChange={(e) => setFormState({ ...formState, saleNumber: e.target.value })}
                placeholder="NW-ABC-20251130-001 or leave empty"
              />
            </AdminFormGroup>
          )}

          <FormRow>
            <AdminFormGroup>
              <AdminFormLabel>Field</AdminFormLabel>
              {!editedConfig && (
                <SearchInput
                  type="text"
                  placeholder="Search fields..."
                  value={fieldSearch}
                  onChange={(e) => setFieldSearch(e.target.value)}
                />
              )}
              <AdminFormSelect
                value={formState.fieldId}
                onChange={(e) => handleFieldChange(e.target.value)}
                disabled={!!editedConfig}
              >
                <option value="">Select field...</option>
                {filteredFields.map(field => (
                  <option key={field.id} value={field.id}>
                    {field.name} ({field.state})
                  </option>
                ))}
              </AdminFormSelect>
            </AdminFormGroup>
            <AdminFormGroup>
              <AdminFormLabel>Operation</AdminFormLabel>
              {!editedConfig && (
                <SearchInput
                  type="text"
                  placeholder="Search operations..."
                  value={operationSearch}
                  onChange={(e) => setOperationSearch(e.target.value)}
                />
              )}
              <AdminFormSelect
                value={formState.operationRecordId}
                onChange={(e) => handleOperationChange(e.target.value)}
                disabled={!!editedConfig}
              >
                <option value="">Select operation...</option>
                {filteredOperations.map(op => (
                  <option key={op.id} value={op.id}>
                    {getOperationName(op.operationId)} ({op.uniqueCode})
                  </option>
                ))}
              </AdminFormSelect>
            </AdminFormGroup>
          </FormRow>

          {selectedOperation && (
            <InfoBox>
              <strong>Operation:</strong> {getOperationName(selectedOperation.operationId)}<br />
              <strong>Code:</strong> {selectedOperation.uniqueCode}<br />
              <strong>Devices:</strong> {selectedOperation.devices.length}
            </InfoBox>
          )}

          {/* Новый режим создания - автоматический матчинг устройств */}
          {!editedConfig && deviceMatching && (
            <Section>
              <SectionTitle>
                Required Devices ({deviceMatching.requiredDevices.filter(d => d.matchedFmd).length}/{deviceMatching.requiredDevices.length} matched)
              </SectionTitle>
              {loadingDevices ? (
                <AdminLoadingMessage>Matching devices...</AdminLoadingMessage>
              ) : (
                deviceMatching.requiredDevices.map((device) => (
                  <DeviceCard key={device.entryId} style={{ opacity: device.matchedFmd ? 1 : 0.5 }}>
                    <DeviceHeader>
                      <DeviceTitle>
                        {device.deviceName}
                        {device.isCritical && <QueueBadge style={{ background: '#ff4444' }}>Critical</QueueBadge>}
                        {device.matchedFmd && <QueueBadge style={{ background: '#44ff44', color: '#000' }}>Matched</QueueBadge>}
                      </DeviceTitle>
                    </DeviceHeader>
                    <FormRow>
                      <SmallFormGroup>
                        <SmallLabel>Required Type</SmallLabel>
                        <span style={{ color: '#fff', fontSize: '12px' }}>{device.deviceType}</span>
                      </SmallFormGroup>
                      <SmallFormGroup>
                        <SmallLabel>Model</SmallLabel>
                        <span style={{ color: '#fff', fontSize: '12px' }}>{device.model}</span>
                      </SmallFormGroup>
                      <SmallFormGroup>
                        <SmallLabel>Assigned Device</SmallLabel>
                        <span style={{ color: device.matchedFmd ? '#44ff44' : '#ff4444', fontSize: '12px', fontWeight: 'bold' }}>
                          {device.matchedFmd ? device.matchedFmd.deviceCode : 'NOT AVAILABLE'}
                        </span>
                      </SmallFormGroup>
                    </FormRow>
                  </DeviceCard>
                ))
              )}
              {!deviceMatching.allDevicesMatched && (
                <AdminErrorMessage>
                  Some devices are not available. Create more Field Mission Devices first.
                </AdminErrorMessage>
              )}
            </Section>
          )}

          {/* Режим редактирования - показываем привязанные устройства */}
          {editedConfig && fieldMissionDevices.length > 0 && (
            <Section>
              <SectionTitle>Assigned Devices ({fieldMissionDevices.length})</SectionTitle>
              {fieldMissionDevices.map((fmd) => (
                <DeviceCard key={fmd.id}>
                  <DeviceHeader>
                    <DeviceTitle>{fmd.deviceCode}</DeviceTitle>
                  </DeviceHeader>
                  <FormRow>
                    <SmallFormGroup>
                      <SmallLabel>Device Type</SmallLabel>
                      <span style={{ color: '#fff', fontSize: '12px' }}>{fmd.deviceType}</span>
                    </SmallFormGroup>
                    <SmallFormGroup>
                      <SmallLabel>Model</SmallLabel>
                      <span style={{ color: '#fff', fontSize: '12px' }}>{fmd.model}</span>
                    </SmallFormGroup>
                  </FormRow>
                </DeviceCard>
              ))}
            </Section>
          )}

          {/* Сообщение если операция не выбрана */}
          {!editedConfig && !deviceMatching && !loadingDevices && formState.operationRecordId && (
            <AdminLoadingMessage>Loading device requirements...</AdminLoadingMessage>
          )}
          {!editedConfig && !deviceMatching && !loadingDevices && !formState.operationRecordId && (
            <AdminEmptyState>Select an operation to see required devices</AdminEmptyState>
          )}

          {error && <AdminErrorMessage>{error}</AdminErrorMessage>}
          {success && <AdminSuccessMessage>{success}</AdminSuccessMessage>}

          <AdminFormActions>
            <AdminSecondaryButton type="button" onClick={() => setModalOpen(false)}>
              Cancel
            </AdminSecondaryButton>
            <AdminPrimaryButton type="submit" disabled={saving}>
              {saving ? 'Saving...' : editedConfig ? 'Update' : 'Sell Operation'}
            </AdminPrimaryButton>
          </AdminFormActions>
        </AdminForm>
      </Modal>
    </Container>
  );
};

export default OperationFieldConfigManager;
